import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash, X, Wrench, MagnifyingGlass, Upload, CheckCircle, XCircle,
  Phone, EnvelopeSimple, MapPin, Buildings, Pencil,
} from "@phosphor-icons/react";

const WRITE_ROLES = new Set(["admin", "gestor_senior"]);

const emptyForm = {
  nombre: "",
  provincia: "",
  cif: "",
  telefonos: [""],
  emails: [""],
  companias: [],
  especialidad: "",
  activo: true,
  multicentro: false,
  notas: "",
};

export default function Reparadores() {
  const { user } = useAuth();
  const canEdit = WRITE_ROLES.has(user?.role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [q, setQ] = useState("");
  const [companiaFilter, setCompaniaFilter] = useState("");
  const [provinciaFilter, setProvinciaFilter] = useState("");
  const [activoFilter, setActivoFilter] = useState("all"); // all | activos | inactivos
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // null | 'new' | rep id
  const [form, setForm] = useState(emptyForm);
  const [saveErr, setSaveErr] = useState("");
  const [saving, setSaving] = useState(false);

  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (companiaFilter) params.compania = companiaFilter;
      if (provinciaFilter) params.provincia = provinciaFilter;
      if (activoFilter === "activos") params.activo = true;
      if (activoFilter === "inactivos") params.activo = false;
      const [list, st, aseg] = await Promise.all([
        api.get("/reparadores", { params }),
        api.get("/reparadores/stats"),
        api.get("/aseguradoras"),
      ]);
      setItems(list.data || []);
      setStats(st.data);
      setAseguradoras(aseg.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando reparadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-cargar al cambiar filtros (con pequeño debounce de búsqueda)
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, companiaFilter, provinciaFilter, activoFilter]);

  const provinciasList = useMemo(
    () => (stats?.por_provincia || []).map((p) => p.provincia),
    [stats],
  );

  const open = (rep = null) => {
    setSaveErr("");
    if (rep) {
      setEditing(rep.id);
      setForm({
        nombre: rep.nombre || "",
        provincia: rep.provincia || "",
        cif: rep.cif || "",
        telefonos: rep.telefonos?.length ? [...rep.telefonos] : [""],
        emails: rep.emails?.length ? [...rep.emails] : [""],
        companias: [...(rep.companias || [])],
        especialidad: rep.especialidad || "",
        activo: rep.activo !== false,
        multicentro: !!rep.multicentro,
        notas: rep.notas || "",
      });
    } else {
      setEditing("new");
      setForm(emptyForm);
    }
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm);
    setSaveErr("");
  };

  const save = async () => {
    setSaveErr("");
    if (!form.nombre.trim()) {
      setSaveErr("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        nombre: form.nombre.trim(),
        provincia: form.provincia.trim() || null,
        cif: form.cif.trim() || null,
        telefonos: form.telefonos.map((t) => t.trim()).filter(Boolean),
        emails: form.emails.map((e) => e.trim()).filter(Boolean),
        especialidad: form.especialidad.trim() || null,
        notas: form.notas.trim() || null,
        companias: form.companias.map((c) => c.toUpperCase()),
      };
      if (editing === "new") {
        await api.post("/reparadores", payload);
        toast.success("Reparador creado");
      } else {
        await api.put(`/reparadores/${editing}`, payload);
        toast.success("Reparador actualizado");
      }
      await load();
      close();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const del = async (rep) => {
    if (!confirm(`¿Eliminar "${rep.nombre}" del directorio?`)) return;
    try {
      await api.delete(`/reparadores/${rep.id}`);
      toast.success("Reparador eliminado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compania = prompt(
      "Código de aseguradora a aplicar a todos los reparadores importados (ej. OCASO, MAPFRE). Deja vacío si el CSV ya trae compañía.",
      "OCASO",
    );
    const fd = new FormData();
    fd.append("file", f);
    try {
      const params = compania ? { default_compania: compania.toUpperCase() } : {};
      const r = await api.post("/reparadores/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      const { imported = 0, updated = 0, total = 0 } = r.data || {};
      toast.success(`Importación: ${imported} nuevos, ${updated} actualizados (de ${total} filas)`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error importando CSV");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="reparadores-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">
            {canEdit ? "Administración" : "Consulta"}
          </div>
          <h1 className="font-display font-bold text-lg tracking-tight">
            Reparadores · {stats?.total ?? 0}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onImport}
                className="hidden"
                data-testid="reparadores-import-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="reparadores-import-button"
                className="text-xs border border-border px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5 hover:bg-surface"
              >
                <Upload className="w-3.5 h-3.5" weight="bold" /> Importar CSV
              </button>
              <button
                onClick={() => open(null)}
                data-testid="new-reparador-button"
                className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo reparador
              </button>
            </>
          )}
        </div>
      </header>

      {/* Stats compactas */}
      {stats && (
        <div className="px-6 pt-4">
          <div className="flex flex-wrap gap-2">
            <StatChip label="Total" value={stats.total} active={!companiaFilter} onClick={() => setCompaniaFilter("")} />
            {stats.por_compania.map((c) => (
              <StatChip
                key={c.compania}
                label={c.compania}
                value={c.count}
                active={companiaFilter === c.compania}
                onClick={() => setCompaniaFilter(companiaFilter === c.compania ? "" : c.compania)}
                testid={`stat-${c.compania}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="px-6 pt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted" weight="bold" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="reparador-search"
            placeholder="Buscar por nombre, CIF, teléfono o email..."
            className="w-full border border-border rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
          />
        </div>
        <select
          value={provinciaFilter}
          onChange={(e) => setProvinciaFilter(e.target.value)}
          data-testid="reparador-provincia-filter"
          className="border border-border rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">Todas las provincias</option>
          {provinciasList.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          value={activoFilter}
          onChange={(e) => setActivoFilter(e.target.value)}
          data-testid="reparador-activo-filter"
          className="border border-border rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="all">Activos e inactivos</option>
          <option value="activos">Sólo activos</option>
          <option value="inactivos">Sólo inactivos</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="text-sm text-muted py-8">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted">
            <Wrench className="w-6 h-6 mx-auto mb-2 text-muted" weight="duotone" />
            No hay reparadores que coincidan con los filtros.
          </div>
        ) : (
          <div className="bg-white border border-[#E8EAED] rounded-[10px] overflow-hidden">
            <table className="w-full text-sm" data-testid="reparadores-table">
              <thead className="thead-modern">
                <tr>
                  <th>Nombre</th>
                  <th>Provincia</th>
                  <th>Compañías</th>
                  <th>Contacto</th>
                  <th className="!text-center">Estado</th>
                  <th className="!w-20"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr
                    key={r.id}
                    className={`border-t border-[#F1F3F5] hover:bg-[#F5F6F8] cursor-pointer group transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}
                    onClick={() => open(r)}
                    data-testid={`reparador-row-${r.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-semibold tracking-tight">{r.nombre}</div>
                      {r.cif && (
                        <div className="text-[11px] tabular-nums text-muted mt-0.5">CIF: {r.cif}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {r.provincia ? (
                        <span className="inline-flex items-center gap-1 text-muted">
                          <MapPin className="w-3 h-3" weight="bold" /> {r.provincia}
                        </span>
                      ) : (
                        <span className="text-muted/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(r.companias || []).length === 0 ? (
                          <span className="text-[11px] text-muted/60 italic">sin asignar</span>
                        ) : (
                          r.companias.map((c) => (
                            <span
                              key={c}
                              className="text-[10px] font-semibold bg-brand/10 text-brand px-1.5 py-0.5 rounded"
                            >
                              {c}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <div className="space-y-0.5">
                        {r.telefonos?.[0] && (
                          <div className="tabular-nums text-foreground inline-flex items-center gap-1">
                            <Phone className="w-3 h-3 text-muted" weight="bold" /> {r.telefonos[0]}
                            {r.telefonos.length > 1 && (
                              <span className="text-muted/60">+{r.telefonos.length - 1}</span>
                            )}
                          </div>
                        )}
                        {r.emails?.[0] && (
                          <div className="text-muted inline-flex items-center gap-1 truncate max-w-[220px]">
                            <EnvelopeSimple className="w-3 h-3" weight="bold" /> {r.emails[0]}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {r.activo ? (
                        <CheckCircle className="w-3.5 h-3.5 text-status-success-fg mx-auto" weight="fill" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-muted mx-auto" weight="fill" />
                      )}
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="opacity-0 group-hover:opacity-100 flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); open(r); }}
                          data-testid={`edit-reparador-${r.id}`}
                          className="p-1 text-muted hover:text-foreground"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" weight="bold" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={(e) => { e.stopPropagation(); del(r); }}
                            data-testid={`del-reparador-${r.id}`}
                            className="p-1 text-muted hover:text-status-error-fg"
                            title="Eliminar"
                          >
                            <Trash className="w-3.5 h-3.5" weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer / Modal Edición */}
      {editing && (
        <ReparadorDrawer
          editing={editing}
          form={form}
          setForm={setForm}
          aseguradoras={aseguradoras}
          canEdit={canEdit}
          saving={saving}
          saveErr={saveErr}
          onClose={close}
          onSave={save}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all inline-flex items-center gap-1.5 ${
        active
          ? "bg-foreground text-white border-foreground"
          : "bg-white text-muted border-border hover:border-foreground hover:text-foreground"
      }`}
    >
      <Buildings className="w-3 h-3" weight="bold" />
      {label}
      <span className={`font-mono ${active ? "text-white" : "text-foreground"}`}>{value}</span>
    </button>
  );
}

function ReparadorDrawer({ editing, form, setForm, aseguradoras, canEdit, saving, saveErr, onClose, onSave }) {
  const updateList = (key, idx, value) => {
    const arr = [...form[key]];
    arr[idx] = value;
    setForm({ ...form, [key]: arr });
  };
  const addToList = (key) => setForm({ ...form, [key]: [...form[key], ""] });
  const removeFromList = (key, idx) =>
    setForm({ ...form, [key]: form[key].filter((_, i) => i !== idx) });

  const toggleCompania = (code) => {
    const has = form.companias.includes(code);
    setForm({
      ...form,
      companias: has ? form.companias.filter((c) => c !== code) : [...form.companias, code],
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-stretch md:items-center md:justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white w-full md:max-w-lg h-full md:h-[calc(100%-2rem)] md:m-4 md:rounded-lg shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="reparador-drawer"
      >
        <div className="h-14 px-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">
              {editing === "new" ? "Nuevo" : "Editar"}
            </div>
            <div className="font-display font-bold text-base tracking-tight">
              Reparador
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-muted hover:text-foreground">
            <X className="w-4 h-4" weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <fieldset disabled={!canEdit} className="space-y-4 disabled:opacity-80">
            <Field
              label="Nombre / razón social *"
              value={form.nombre}
              onChange={(v) => setForm({ ...form, nombre: v })}
              testid="rep-form-nombre"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Provincia"
                value={form.provincia}
                onChange={(v) => setForm({ ...form, provincia: v })}
                testid="rep-form-provincia"
              />
              <Field
                label="CIF"
                value={form.cif}
                onChange={(v) => setForm({ ...form, cif: v })}
                testid="rep-form-cif"
                mono
              />
            </div>
            <Field
              label="Especialidad"
              value={form.especialidad}
              onChange={(v) => setForm({ ...form, especialidad: v })}
              testid="rep-form-especialidad"
              placeholder="Ej. fontanería, cristalería, electricidad..."
            />

            {/* Compañías */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-muted mb-1.5">
                Compañías con las que trabaja
              </label>
              <div className="flex flex-wrap gap-1.5">
                {aseguradoras.map((a) => {
                  const sel = form.companias.includes(a.code);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleCompania(a.code)}
                      data-testid={`rep-form-compania-${a.code}`}
                      className={`text-[11px] font-semibold px-2 py-1 rounded border transition-all ${
                        sel
                          ? "bg-brand text-white border-brand"
                          : "bg-white text-muted border-border hover:border-foreground"
                      }`}
                    >
                      {a.name}
                    </button>
                  );
                })}
              </div>
              {form.companias.length === 0 && (
                <div className="text-[11px] text-muted italic mt-1">Ninguna seleccionada.</div>
              )}
            </div>

            {/* Teléfonos */}
            <ListInput
              label="Teléfonos"
              values={form.telefonos}
              onChange={(i, v) => updateList("telefonos", i, v)}
              onAdd={() => addToList("telefonos")}
              onRemove={(i) => removeFromList("telefonos", i)}
              icon={Phone}
              testidPrefix="rep-form-tel"
              mono
            />

            {/* Emails */}
            <ListInput
              label="Emails"
              values={form.emails}
              onChange={(i, v) => updateList("emails", i, v)}
              onAdd={() => addToList("emails")}
              onRemove={(i) => removeFromList("emails", i)}
              icon={EnvelopeSimple}
              testidPrefix="rep-form-email"
            />

            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  data-testid="rep-form-activo"
                />
                Activo
              </label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.multicentro}
                  onChange={(e) => setForm({ ...form, multicentro: e.target.checked })}
                  data-testid="rep-form-multicentro"
                />
                Multicentro
              </label>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-muted mb-1">
                Notas
              </label>
              <textarea
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                rows={3}
                data-testid="rep-form-notas"
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
              />
            </div>

            {saveErr && (
              <div className="text-xs text-status-error-fg bg-status-error-bg border border-status-error-bd px-2 py-1.5 rounded">
                {saveErr}
              </div>
            )}
          </fieldset>
        </div>

        {canEdit && (
          <div className="h-14 px-5 border-t border-border flex items-center justify-end gap-2">
            <button onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">
              Cancelar
            </button>
            <button
              onClick={onSave}
              disabled={saving || !form.nombre.trim()}
              data-testid="rep-form-save"
              className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Plus className="w-3 h-3" weight="bold" /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, mono, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest font-semibold text-muted mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground ${
          mono ? "font-mono" : ""
        }`}
      />
    </div>
  );
}

function ListInput({ label, values, onChange, onAdd, onRemove, icon: Icon, testidPrefix, mono }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-muted">
          {label}
        </label>
        <button
          type="button"
          onClick={onAdd}
          data-testid={`${testidPrefix}-add`}
          className="text-[11px] text-muted hover:text-foreground inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" weight="bold" /> Añadir
        </button>
      </div>
      <div className="space-y-1.5">
        {values.map((v, i) => (
          // Controlled inputs gestionados por el padre, sin reordenación: el índice
          // como key es estable y correcto (cambiar de v rompería el focus al editar).
          <div key={`${testidPrefix}-row-${i}`} className="flex items-center gap-2">
            {Icon && <Icon className="w-3.5 h-3.5 text-muted flex-shrink-0" weight="bold" />}
            <input
              type="text"
              value={v}
              onChange={(e) => onChange(i, e.target.value)}
              data-testid={`${testidPrefix}-${i}`}
              className={`flex-1 border border-border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground ${
                mono ? "font-mono" : ""
              }`}
            />
            {values.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="p-1 text-muted hover:text-status-error-fg"
                data-testid={`${testidPrefix}-${i}-remove`}
              >
                <Trash className="w-3 h-3" weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
